import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { Box, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo } from "react";
import type { ColDef, RowClassRules } from "ag-grid-community";
import type { CrewPivotRow } from "../models/WeeklyIncentiveData";
import { useIncThresholds } from "../hooks/useIncThresholds";
import { IncPercentPill } from "./IncPercentPill";
import { ROW_TINTS } from "../constants/colors";

type Props = {
	crewPivot: CrewPivotRow[];
};

const pillCellStyle = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
};

export const CrewIncentivesPivot = ({ crewPivot }: Props) => {
	const { defaultColDef } = useGridDefinitions({});
	const { thresholds: incThresholds } = useIncThresholds();

	/** Crew fields that are empty on the Total row (label spans across them). */
	const INDIVIDUAL_CREW_FIELDS = new Set(["crewA", "crewB", "crewC", "crewD"]);

	/** Creates a cell renderer that checks futureKeys for the given field. */
	const makePillRenderer = useCallback(
		(fieldKey: string) =>
			function PillCell(params: { value: number | null; data: CrewPivotRow }) {
				if (params.data?.isTotal && INDIVIDUAL_CREW_FIELDS.has(fieldKey))
					return null;
				if (params.value == null)
					return <span style={{ color: "#9e9e9e" }}>—</span>;
				return (
					<IncPercentPill
						value={params.value}
						thresholds={incThresholds}
						isFuture={params.data?.futureKeys?.has(fieldKey)}
					/>
				);
			},
		[incThresholds],
	);

	const allRows = crewPivot;

	const columnDefs = useMemo<ColDef<CrewPivotRow>[]>(
		() => [
			{
				headerName: "Date",
				field: "date",
				width: 100,
				colSpan: (params) => (params.data?.isTotal ? 5 : 1),
				cellRenderer: (params: { value: string; data: CrewPivotRow }) => {
					if (params.data?.isTotal) {
						return (
							<div
								style={{ width: "100%", textAlign: "right", fontWeight: 600 }}
							>
								Total
							</div>
						);
					}
					return <span style={{ fontWeight: 600 }}>{params.value}</span>;
				},
			},
			{
				headerName: "A",
				field: "crewA",
				width: 80,
				cellRenderer: makePillRenderer("crewA"),
				cellStyle: pillCellStyle,
			},
			{
				headerName: "B",
				field: "crewB",
				width: 80,
				cellRenderer: makePillRenderer("crewB"),
				cellStyle: pillCellStyle,
			},
			{
				headerName: "C",
				field: "crewC",
				width: 80,
				cellRenderer: makePillRenderer("crewC"),
				cellStyle: pillCellStyle,
			},
			{
				headerName: "D",
				field: "crewD",
				width: 80,
				cellRenderer: makePillRenderer("crewD"),
				cellStyle: pillCellStyle,
			},
			{
				headerName: "Tot A/B",
				field: "totalAB",
				width: 85,
				cellRenderer: makePillRenderer("totalAB"),
				cellStyle: pillCellStyle,
			},
			{
				headerName: "Tot C/D",
				field: "totalCD",
				width: 85,
				cellRenderer: makePillRenderer("totalCD"),
				cellStyle: pillCellStyle,
			},
		],
		[makePillRenderer],
	);

	const rowClassRules = useMemo<RowClassRules<CrewPivotRow>>(
		() => ({
			"incentive-row-total": (params) => params.data?.isTotal === true,
		}),
		[],
	);

	return (
		<Paper
			elevation={2}
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				"& .incentive-row-total": {
					backgroundColor: `${ROW_TINTS.total} !important`,
					fontWeight: 700,
				},
			}}
		>
			<Box
				sx={{
					px: 2,
					py: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
					Crew Incentives by Day / Week
				</Typography>
			</Box>
			<Box>
				<AgGridReact<CrewPivotRow>
					rowData={allRows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					rowHeight={34}
					headerHeight={34}
					animateRows
					getRowId={(params) => params.data.date}
					rowClassRules={rowClassRules}
					domLayout="autoHeight"
					suppressCellFocus
					suppressContextMenu
				/>
			</Box>
		</Paper>
	);
};
